import { Button } from 'designSystem/components/Button'
import { FavoriteSearch } from 'features/knowledgeDocumentViewer/utils/favoriteSearchesStorage'
import { SearchQueryHistoryEntry } from 'features/knowledgeDocumentViewer/utils/searchQueryHistoryStorage'
import { ArrowUp, Search } from 'lucide-react'
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'

import { SearchSuggestionsDropdown } from './SearchSuggestionsDropdown'

type AgenticSearchBoxVariant = 'landing' | 'compact'
type AgenticSearchBoxSurface = 'default' | 'plain'
type AgenticSearchBoxChangeEvent = React.ChangeEvent<
  HTMLInputElement | HTMLTextAreaElement
>
const COMPACT_TEXTAREA_LINE_HEIGHT_PX = 20
const COMPACT_TEXTAREA_MAX_LINES = 5
const COMPACT_TEXTAREA_MAX_HEIGHT_PX =
  COMPACT_TEXTAREA_LINE_HEIGHT_PX * COMPACT_TEXTAREA_MAX_LINES

const BoxRoot = styled.div<{
  $surface: AgenticSearchBoxSurface
  $variant: AgenticSearchBoxVariant
}>`
  position: relative;
  z-index: 40;
  width: 100%;
  display: flex;
  border-radius: 0.5rem;
  ${(props) =>
    props.$surface === 'default'
      ? `
        border: 0.0625rem solid ${props.theme.forecast.border2};
        background: ${props.theme.forecast.background3};
        box-shadow:
          0 0 0 0.5rem rgba(245, 245, 245, 0.06),
          0 0.0625rem 0.125rem rgba(26, 26, 26, 0.05);
      `
      : `
        border: 0.0625rem solid ${props.theme.forecast.border2};
        background: ${props.theme.forecast.background3};
        box-shadow: none;
      `}
  ${(props) =>
    props.$variant === 'landing'
      ? `
        min-height: 8rem;
        flex-direction: column;
      `
      : `
        min-height: auto;
        flex-direction: column;
      `}
`

const PromptArea = styled.div`
  height: 5rem;
  display: flex;
  align-items: center;
  padding: 1rem 0.75rem;
  border-bottom: 0.0625rem solid ${(props) => props.theme.forecast.border2};
`

const PromptSearchIconWrapper = styled.span`
  display: inline-flex;
  margin-right: 0.5rem;
  color: ${(props) => props.theme.forecast.secondaryText};
  flex-shrink: 0;
`

const PromptInput = styled.input`
  width: 100%;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: ${(props) => props.theme.forecast.defaultText};
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.25rem;

  &::placeholder {
    color: ${(props) => props.theme.forecast.secondaryText};
  }

  &::-webkit-search-cancel-button {
    display: none;
  }
`

const CompactPromptTextarea = styled.textarea`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  height: ${COMPACT_TEXTAREA_LINE_HEIGHT_PX}px;
  min-height: ${COMPACT_TEXTAREA_LINE_HEIGHT_PX}px;
  max-height: ${COMPACT_TEXTAREA_MAX_HEIGHT_PX}px;
  padding: 0;
  border: 0;
  outline: 0;
  resize: none;
  background: transparent;
  color: ${(props) => props.theme.forecast.defaultText};
  font-size: 0.875rem;
  font-weight: 400;
  line-height: ${COMPACT_TEXTAREA_LINE_HEIGHT_PX}px;
  overflow-y: auto;

  &::placeholder {
    color: ${(props) => props.theme.forecast.secondaryText};
  }
`

const CompactInputArea = styled.div`
  min-width: 0;
  min-height: 3.25rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.625rem 0.75rem;
`

const ControlsRow = styled.div`
  min-height: 3rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem;
`

const ControlsLeft = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const CompactControlsSpacer = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const SubmitButton = styled.button<{ $compact?: boolean }>`
  box-sizing: border-box;
  height: 2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: flex-end;
  gap: 0.375rem;
  flex-shrink: 0;
  padding: ${(props) => (props.$compact ? '0 0.625rem' : '0 0.75rem')};
  border: 0;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 0.0625rem 0.0625rem
    color-mix(in oklch, var(--foreground) 5%, transparent);

  /* Compact/side-panel: primary filled control. Landing keeps the light chip. */
  background: ${(props) =>
    props.$compact ? 'var(--primary)' : 'var(--muted)'};
  color: ${(props) =>
    props.$compact ? 'var(--primary-foreground)' : 'var(--foreground)'};

  svg {
    display: block;
    flex-shrink: 0;
  }

  &:hover {
    background: ${(props) =>
      props.$compact
        ? 'color-mix(in oklch, var(--primary) 80%, transparent)'
        : 'color-mix(in oklch, var(--muted) 70%, var(--foreground) 8%)'};
  }

  &:focus-visible {
    outline: 0.125rem solid var(--ring);
    outline-offset: 0.125rem;
  }
`

export interface AgenticSearchBoxProps {
  searchInput: string
  onSearchChange: (e: AgenticSearchBoxChangeEvent) => void
  onSearchKeyDown?: (e: React.KeyboardEvent) => void
  onSearchSubmit: () => void
  placeholder: string
  suggestions?: SearchQueryHistoryEntry[]
  onSuggestionSelect?: (query: string) => void
  onClearHistory?: () => void
  favoriteSearches?: FavoriteSearch[]
  isSearchFavorited?: (query: string) => boolean
  onToggleSearchFavorite?: (query: string, contentFilter: string) => void
  contentFilter?: string
  variant?: AgenticSearchBoxVariant
  rightContent?: React.ReactNode
  surface?: AgenticSearchBoxSurface
}

export function AgenticSearchBox({
  searchInput,
  onSearchChange,
  onSearchKeyDown,
  onSearchSubmit,
  placeholder,
  suggestions,
  onSuggestionSelect,
  onClearHistory,
  favoriteSearches,
  isSearchFavorited,
  onToggleSearchFavorite,
  contentFilter,
  variant = 'landing',
  rightContent,
  surface = 'default',
}: AgenticSearchBoxProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const pinnedCount = favoriteSearches?.length ?? 0
  const recentCount = suggestions?.length ?? 0
  const totalCount = pinnedCount + recentCount
  const hasSuggestions = totalCount > 0

  const resizeCompactTextarea = useCallback(() => {
    const textarea = textareaRef.current
    if (!textarea) return
    textarea.style.height = 'auto'
    const nextHeight = Math.min(
      Math.max(textarea.scrollHeight, COMPACT_TEXTAREA_LINE_HEIGHT_PX),
      COMPACT_TEXTAREA_MAX_HEIGHT_PX
    )
    textarea.style.height = `${nextHeight}px`
  }, [])

  const handleFocus = useCallback(() => {
    if (suggestions !== undefined) {
      setIsDropdownOpen(true)
      setHighlightedIndex(-1)
    }
  }, [suggestions])

  const handleChange = useCallback(
    (e: AgenticSearchBoxChangeEvent) => {
      onSearchChange(e)
      if (suggestions !== undefined) {
        setIsDropdownOpen(true)
      }
    },
    [onSearchChange, suggestions]
  )

  const handleBlur = useCallback(() => {
    setTimeout(() => {
      if (!wrapperRef.current?.contains(document.activeElement)) {
        setIsDropdownOpen(false)
        setHighlightedIndex(-1)
      }
    }, 150)
  }, [])

  const handleSelect = useCallback(
    (query: string) => {
      onSuggestionSelect?.(query)
      setIsDropdownOpen(false)
      setHighlightedIndex(-1)
    },
    [onSuggestionSelect]
  )

  const handleClearHistory = useCallback(() => {
    onClearHistory?.()
    setIsDropdownOpen(false)
  }, [onClearHistory])

  const handleSubmit = useCallback(() => {
    setIsDropdownOpen(false)
    setHighlightedIndex(-1)
    inputRef.current?.blur()
    textareaRef.current?.blur()
    onSearchSubmit()
  }, [onSearchSubmit])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isDropdownOpen || !hasSuggestions) {
        if (e.key === 'Enter') {
          if (variant === 'compact') {
            e.preventDefault()
          }
          inputRef.current?.blur()
          textareaRef.current?.blur()
        }
        onSearchKeyDown?.(e)
        return
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlightedIndex((prev) => (prev + 1) % totalCount)
        return
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlightedIndex((prev) => (prev <= 0 ? totalCount - 1 : prev - 1))
        return
      }

      if (e.key === 'Enter' && highlightedIndex >= 0) {
        e.preventDefault()
        e.stopPropagation()
        const pinned = favoriteSearches ?? []
        if (highlightedIndex < pinned.length) {
          handleSelect(pinned[highlightedIndex].query)
        } else {
          const recentIndex = highlightedIndex - pinned.length
          const filtered = (suggestions ?? []).filter(
            (s) =>
              !pinned.some(
                (p) => p.query.toLowerCase() === s.query.toLowerCase()
              )
          )
          if (filtered[recentIndex]) {
            handleSelect(filtered[recentIndex].query)
          }
        }
        return
      }

      if (e.key === 'Enter') {
        if (variant === 'compact') {
          e.preventDefault()
        }
        setIsDropdownOpen(false)
        setHighlightedIndex(-1)
        inputRef.current?.blur()
        textareaRef.current?.blur()
        onSearchKeyDown?.(e)
        return
      }

      if (e.key === 'Escape') {
        setIsDropdownOpen(false)
        setHighlightedIndex(-1)
        return
      }

      onSearchKeyDown?.(e)
    },
    [
      isDropdownOpen,
      hasSuggestions,
      totalCount,
      suggestions,
      favoriteSearches,
      highlightedIndex,
      handleSelect,
      onSearchKeyDown,
      variant,
    ]
  )

  useEffect(() => {
    setHighlightedIndex(-1)
  }, [searchInput])

  useLayoutEffect(() => {
    if (variant === 'compact') {
      resizeCompactTextarea()
    }
  }, [resizeCompactTextarea, searchInput, variant])

  const showDropdown =
    isDropdownOpen &&
    suggestions !== undefined &&
    onSuggestionSelect &&
    (hasSuggestions || !searchInput.trim())

  return (
    <BoxRoot
      ref={wrapperRef}
      onBlur={handleBlur}
      $surface={surface}
      $variant={variant}>
      {variant === 'landing' ? (
        <>
          <PromptArea>
            <PromptSearchIconWrapper aria-hidden='true'>
              <Search size='1rem' />
            </PromptSearchIconWrapper>
            <PromptInput
              ref={inputRef}
              type='search'
              value={searchInput}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
              placeholder={placeholder}
            />
          </PromptArea>
          <ControlsRow>
            {rightContent ? <ControlsLeft>{rightContent}</ControlsLeft> : null}
            <Button
              variant='primaryRaised'
              size='default'
              type='button'
              aria-label='Search documents'
              onClick={handleSubmit}>
              <ArrowUp size={14} />
              <span>Search</span>
            </Button>
          </ControlsRow>
        </>
      ) : (
        <CompactInputArea>
          <CompactPromptTextarea
            ref={textareaRef}
            rows={1}
            value={searchInput}
            onChange={(event) => {
              resizeCompactTextarea()
              handleChange(event)
            }}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            placeholder={placeholder}
          />
          {rightContent ? (
            <CompactControlsSpacer>{rightContent}</CompactControlsSpacer>
          ) : null}
          <SubmitButton
            $compact
            type='button'
            aria-label='Search documents'
            onClick={handleSubmit}>
            <ArrowUp size={14} />
          </SubmitButton>
        </CompactInputArea>
      )}
      {showDropdown && (
        <SearchSuggestionsDropdown
          suggestions={suggestions}
          favoriteSearches={favoriteSearches}
          isSearchFavorited={isSearchFavorited}
          onToggleSearchFavorite={onToggleSearchFavorite}
          contentFilter={contentFilter}
          onSelect={handleSelect}
          onClearHistory={handleClearHistory}
          highlightedIndex={highlightedIndex}
        />
      )}
    </BoxRoot>
  )
}
